import { UseMutationResult, useQuery } from "@tanstack/react-query";
import { Button, Card, Select } from "@rumbledirect/ui";
import { Flex, Form } from "antd";
import { useEffect } from "react";
import { ReplacementTransportOption } from "@rumble-it/common-lib/types/models/ReplacementTransportOption.interface";
import { fetchDossierTypesWithoutPagination } from "../../../../../lib/api/resources/dossierTypes";
import { fetchCompanies } from "../../../../../lib/api/resources/companies";
import { fetchReplacementTransportOptions } from "../../../../../lib/api/resources/replacementTransports";
import { Company } from "@rumble-it/common-lib/types/models/Company.interface";
import { ReplacementTransportPlan } from "@rumble-it/common-lib/types/models/ReplacementTransportPlan.interface";

export type ReplacementTransportPlanFormValues = Omit<
  ReplacementTransportPlan,
  "dossierType" | "options" | "company" | "@id" | "id"
> & {
  dossierTypeId: string | null;
  options: string[];
  companyId: string | null;
};

export type ReplacementTransportPlanInputDto = Omit<
  ReplacementTransportPlanFormValues,
  "options"
> & {
  options: { replacementTransportOptionId: string; priority: number }[];
};

interface ReplacementTransportPlanFormCardProps {
  mutation: UseMutationResult<
    ReplacementTransportPlan | null,
    Error,
    ReplacementTransportPlanInputDto,
    unknown
  >;
  initialValues?: ReplacementTransportPlanFormValues;
  loading?: boolean;
  title?: string;
  buttonText?: string;
}

export const ReplacementTransportPlanFormCard = ({
  mutation,
  initialValues,
  title = "Vervangend vervoer plan aanmaken",
  buttonText = "Aanmaken",
  loading = false,
}: ReplacementTransportPlanFormCardProps) => {
  const [form] = Form.useForm();

  const companiesQuery = useQuery({
    queryKey: ["companies", "no-pagination"],
    queryFn: async () =>
      await fetchCompanies({
        pagination: false,
        type: ["lease", "insurance"],
      }),
    select: (data) =>
      data.member.map((option: Company) => ({
        label: option.fullName,
        value: option.id,
      })),
  });

  const dossierTypeQuery = useQuery({
    queryKey: ["dossierTypes", "no-pagination"],
    queryFn: fetchDossierTypesWithoutPagination,
    select: (data) =>
      data.member.find(
        (dossierType) => dossierType.name === "repair_direct.generic",
      ) || null,
  });

  const replacementTransportOptionsQuery = useQuery({
    queryKey: ["replacementTransportOptions"],
    queryFn: fetchReplacementTransportOptions,
    select: (data) =>
      data.member.map((option: ReplacementTransportOption) => ({
        label: option.title,
        value: option.id,
      })),
  });

  const handleFinish = (values: ReplacementTransportPlanFormValues) => {
    const payload = {
      ...values,
      dossierTypeId: dossierTypeQuery.data?.id || null,
      priority: 0,
      options:
        values.options.map((option) => ({
          replacementTransportOptionId: option,
          priority: 0,
        })) || [],
    };
    mutation.mutate(payload);
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical">
      <Flex vertical gap={16}>
        <Card loading={loading} title={title}>
          <Form.Item name="companyId" label="Opdrachtgever">
            <Select
              placeholder="Selecteer een opdrachtgever"
              options={companiesQuery.data}
              loading={companiesQuery.isFetching}
              showSearch
              optionFilterProp="label"
              notFoundContent="Geen opdrachtgevers gevonden"
            />
          </Form.Item>
          <Form.Item
            label="Beschikbare vervangend vervoer opties"
            name="options"
            help="Selecteer de vervangend vervoer opties die beschikbaar moeten zijn voor deze opdrachtgever"
          >
            <Select
              mode="multiple"
              loading={replacementTransportOptionsQuery.isFetching}
              options={replacementTransportOptionsQuery.data}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item label={null}>
            <Button
              loading={mutation.isPending}
              type="primary"
              htmlType="submit"
            >
              {buttonText}
            </Button>
          </Form.Item>
        </Card>
      </Flex>
    </Form>
  );
};
